import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { ApiCustomer, ApiProduct } from '@vend/types'
import { IAngularEvent } from 'angular'
import { useDispatch } from 'react-redux'
import { trackUserEvent } from '@vend/utilities'
import { createSelector } from '@reduxjs/toolkit'
import { useT } from '@transifex/react'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { CustomerModal, ProductModal } from '@vend/business-components'
import { $rootScope } from '../../ng-compat/imports'
import { EVENT } from '../../constants'
import {
  useCustomerManager,
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
} from '../../ng-compat/hooks'
import { updateCalculatingStateOfAllLineItems } from '../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../state/hooks'
import { selectIsFinalisingSale } from '../../state/slices/uiStateSlice'
import { WEB_REGISTER } from '../web-register.constant'
import { RootState } from '../../state/store'
import {
  hasCustomerRelatedPayments,
  isConfirmedNotReadyToComplete,
} from '../../functions/sale'
import { getSaleDisplayStatus } from '../../util/saleStatus'
import { LineItemInput } from '../../models/line-item'

interface AddEmbeddedBarcodeData {
  product: ApiProduct
  barcodeData: number
}

interface UnknownLineItemData {
  query: string
  suggestions: unknown[]
}

const focusSearch = () => {
  setTimeout(() => {
    $rootScope.$emit('autocomplete:global-search:focus')
  })
}

const selectSaleData = createSelector(
  [
    (state: RootState) => state.currentSale.readyToComplete,
    (state: RootState) => state.currentSale.isSubmittedSaleEarly,
    (state: RootState) => state.currentSale.status,
    (state: RootState) => state.currentSale.payments,
  ],
  (readyToComplete, isSubmittedSaleEarly, status, payments) => ({
    readyToComplete,
    isSubmittedSaleEarly,
    status,
    payments,
  })
)

export const SaleEvents: FunctionComponent = () => {
  const t = useT()
  const dispatch = useDispatch()
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const customerManager = useCustomerManager()
  const isFinalisingSale = useAppSelector(selectIsFinalisingSale)
  const saleData = useAppSelector(selectSaleData)

  const [viewProduct, setViewProduct] = useState<ApiProduct | null>(null)
  const [viewCustomer, setViewCustomer] = useState<ApiCustomer | null>(null)

  // Want this to be a callback rather than a value so isActiveRegisterOpen
  // is checked at the time rather than during memoisation
  const canSell = useCallback(() => {
    return registerManager.isActiveRegisterOpen() && !isFinalisingSale
  }, [isFinalisingSale, registerManager])

  useEffect(() => {
    const handleAddProductEvent = (e: IAngularEvent, product: ApiProduct) => {
      if (canSell()) {
        saleManager.addProductToSale(product)
        focusSearch()
        return
      }
      setViewProduct(product)
    }

    const handleEmbeddedBarcodeEvent = (
      e: IAngularEvent,
      { product, barcodeData }: AddEmbeddedBarcodeData
    ) => {
      if (!canSell()) {
        return
      }
      const barcodeOptionType = retailerSettingsManager.getBarcodeOption()
      let options: Partial<LineItemInput> = {}
      if (barcodeOptionType === 'price') {
        options = { price: (barcodeData / 100).toString() }
      } else if (barcodeOptionType === 'weight') {
        options = { quantity: barcodeData / 1000 }
      }

      saleManager.addProductToSale(product, options, false)
      focusSearch()
    }

    const handleCalculationComplete = () => {
      dispatch(updateCalculatingStateOfAllLineItems(false))
    }

    const handleUnknownProductEvent = (
      e: IAngularEvent,
      data: UnknownLineItemData
    ) => {
      if (!canSell()) {
        return
      }
      if (!data.suggestions.length) {
        saleManager.addUnknownProductToSale(data.query)
        focusSearch()
        trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Unknown Product Added', {
          query: data.query,
        })
      }
    }

    const handleCustomerEvent = async (
      e: IAngularEvent,
      customer: ApiCustomer
    ) => {
      if (!canSell()) {
        setViewCustomer(customer)
        return
      }

      if (isConfirmedNotReadyToComplete(saleData)) {
        const statusDisplayName = getSaleDisplayStatus(saleData, '')
        const retrivedSaleMessage = t(
          'You are currently viewing a retrieved {status} sale. The customer can not be changed',
          { status: statusDisplayName }
        )
        ToastNotifications.negative(retrivedSaleMessage)
        focusSearch()
        return
      }

      if (hasCustomerRelatedPayments(saleData.payments)) {
        ToastNotifications.negative(
          t(
            'The current sale has been partially paid. The customer can not be changed.'
          )
        )
        focusSearch()
        return
      }
      // Ensure we have the latest details before adding the customer to the sale
      focusSearch()
      const latest = await customerManager.refreshCustomer(customer)
      return saleManager.addCustomer(latest as ApiCustomer)
    }

    const removeAddProductListener = $rootScope.$on(
      EVENT.SEARCH_PRODUCT_SELECTED,
      handleAddProductEvent
    )

    const removeEmbeddedBarcodeListener = $rootScope.$on(
      'search:embedded-barcode-selected',
      handleEmbeddedBarcodeEvent
    )

    const removeUnknownProductListener = $rootScope.$on(
      'search:unknown-product',
      handleUnknownProductEvent
    )

    const removeCustomerListener = $rootScope.$on(
      'search:customer-selected',
      handleCustomerEvent
    )

    saleManager.on('saleManager:saleCalculating:end', handleCalculationComplete)

    return () => {
      removeAddProductListener()
      removeEmbeddedBarcodeListener()
      removeUnknownProductListener()
      removeCustomerListener()

      saleManager.off(
        'saleManager:saleCalculating:end',
        handleCalculationComplete
      )
    }
  }, [
    canSell,
    customerManager,
    dispatch,
    isFinalisingSale,
    registerManager,
    retailerSettingsManager,
    saleData,
    saleManager,
    t,
  ])

  return (
    <>
      {viewProduct ? (
        <ProductModal
          onClose={() => setViewProduct(null)}
          productId={viewProduct.id}
        />
      ) : null}
      {viewCustomer ? (
        <CustomerModal
          onClose={() => setViewCustomer(null)}
          onError={() => setViewCustomer(null)}
          customerId={viewCustomer.id}
        />
      ) : null}
    </>
  )
}
